import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import store, { RootState } from '../store';
import { useSearchParams } from 'react-router-dom';

import './Revenue.pcss';
import Modal from '../components/Modal';
import { PubPicker } from '../components/PubPicker';
import { DatePicker } from '../components/DatePicker';
import { ConfirmSave } from '../components/ConfirmSave';
import { DataTablePagination } from '../components/DataTablePagination';
import { EditReportForm } from '../components/EditReportForm';
import { fetchAdsByPlacement } from '../store/adsByPlacement';
import { fetchAdsByRecipe } from '../store/adsByRecipe';
import { fetchAdsByIngredient } from '../store/adsByIngredient';
import { fetchAdsByPublisher } from '../store/adsByPub';
import { confirmingSchedule, creatingSchedule } from '../store/scheduleModals';
import { downloadCSV } from '../utils';
import { sendEvent } from '../utils/analytics';
import { ReportCategory, ReportGrouping } from '../types';
import { growl } from '../store/growl';
import { toast } from '../store/toast';
import { Icon } from '../components/icons';
import { Tooltip } from '../components/Tooltip';

export const Revenue: () => React.JSX.Element = () => {
  const dispatch = useDispatch<typeof store.dispatch>();
  // Subscribe to store
  const user = useSelector((state: RootState) => state.authenticatedUser);
  const allPublisherData = useSelector(
    (state: RootState) => state.adsByPublisher
  );
  const allIngredientData = useSelector(
    (state: RootState) => state.adsByIngredient
  );
  const allRecipeData = useSelector((state: RootState) => state.adsByRecipe);
  const allPlacementData = useSelector(
    (state: RootState) => state.adsByPlacement
  );
  const { selectedPubs, availablePubs } = useSelector(
    (state: RootState) => state.pubs
  );
  const storeDates = useSelector((state: RootState) => state.dateRange);
  const currentlyCreatingSchedule = useSelector(
    (state: RootState) => state.creatingSchedule
  );
  const currentlyConfirmingSchedule = useSelector(
    (state: RootState) => state.confirmingSchedule
  );
  // Feature flags
  const { showImpressions } = useSelector(
    (state: RootState) => state.featureFlags
  );

  // Schedule Reports
  const [grouping, setGrouping] = useState<ReportGrouping>('by_recipe');
  const [category, setCategory] = useState<ReportCategory>('revenue');

  // Subscribe to url params
  const [searchParams] = useSearchParams();
  const fromDateInParams = searchParams.get('from');
  const toDateInParams = searchParams.get('to');

  const [isDownloading, setIsDownloading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (
      fromDateInParams === storeDates.from &&
      toDateInParams === storeDates.to &&
      availablePubs !== null
    ) {
      (async () => {
        try {
          dispatch(fetchAdsByPlacement(50));
          dispatch(fetchAdsByRecipe(50));
          dispatch(fetchAdsByIngredient(50));
          dispatch(fetchAdsByPublisher(50));
        } catch (error) {
          console.error('Revenue report page error.', error);
        }
      })();
    }
  }, [
    dispatch,
    storeDates.from,
    storeDates.to,
    fromDateInParams,
    toDateInParams,
    availablePubs,
    selectedPubs,
  ]);

  const download = (path: string, fileNamePrefix: string) => {
    return async () => {
      setIsDownloading(true);
      dispatch(toast('Downloading your report, this may take some time.'));
      try {
        if (storeDates.from === null || storeDates.to === null) {
          throw new Error('Date range in store is unexpectedly null.');
        }
        await downloadCSV({
          path,
          fileNamePrefix,
          from: storeDates.from,
          to: storeDates.to,
          pubs: selectedPubs,
          reportStyle: 'revenue',
        });
        sendEvent({
          user: user,
          publishers: availablePubs,
          name: 'portal_report_download',
          start_date: storeDates.from,
          end_date: storeDates.to,
          current_page: 'reports_revenue',
          report_name: fileNamePrefix,
        });
      } catch (error) {
        dispatch(
          growl(
            'The data was too large to be downloaded directly. Please use the “Schedule Reports” button instead.'
          )
        );
        console.error('Revenue report download error.', error);
      }
      setIsDownloading(false);
      dispatch(toast(null));
    };
  };

  return (
    <>
      <div id="revenue">
        {/* header */}
        <div id="revenue-header">
          <div id="revenue-header__left">
            <div id="revenue-header__welcome">
              Revenue
              <div className="w-spacer" />
              <Icon
                name="help-circle"
                style={{ marginBottom: '-4px', cursor: 'pointer' }}
                onClick={() => setShowTooltip((prev) => !prev)}
              />
              {showTooltip && (
                <Tooltip
                  location="reportRevenue"
                  onClick={() => {
                    setShowTooltip((prev) => !prev);
                  }}
                />
              )}
            </div>

            <div id="revenue-header__greeting-msg">
              Displaying the top 50 results. Download for full data.
            </div>
          </div>
          <div id="revenue-header__right">
            <PubPicker />
            <div className="w-spacer" />
            <DatePicker />
          </div>
        </div>
        {/* 1. By Placement */}
        <div id="revenue-section1">
          <DataTablePagination
            data={allPlacementData}
            title="Revenue by Placement"
            view="revenuePlacements"
            isDownloading={isDownloading}
            onDownload={download(
              '/reporting/ads/placement',
              'Chicory_revenue_by_placement'
            )}
            onSchedule={() => {
              setCategory('revenue');
              setGrouping('by_placement');
              dispatch(creatingSchedule(true));
            }}
          />
        </div>
        {/* 2. By Recipe */}
        <div id="revenue-section2">
          <DataTablePagination
            data={allRecipeData}
            title="Revenue by Recipe"
            view="revenueRecipes"
            isDownloading={isDownloading}
            onDownload={download(
              '/reporting/ads/recipe',
              'Chicory_revenue_by_recipe'
            )}
            onSchedule={() => {
              setCategory('revenue');
              setGrouping('by_recipe');
              dispatch(creatingSchedule(true));
            }}
          />
        </div>
        {/* 3. By Ingredient */}
        {showImpressions && (
          <div id="revenue-section3">
            <DataTablePagination
              data={allIngredientData}
              title="Revenue by Ingredient"
              view="revenueIngredients"
              isDownloading={isDownloading}
              onDownload={download(
                '/reporting/ads/ingredient',
                'Chicory_revenue_by_ingredient'
              )}
              onSchedule={() => {
                setCategory('revenue');
                setGrouping('by_ingredient');
                dispatch(creatingSchedule(true));
              }}
            />
          </div>
        )}
        {/* 4. By Domain */}
        <div id="revenue-section4">
          <DataTablePagination
            data={allPublisherData}
            title="Revenue by Domain"
            isDownloading={isDownloading}
            view="revenuePublishers"
            onDownload={download(
              '/reporting/ads/publisher',
              'Chicory_revenue_by_publisher'
            )}
            onSchedule={() => {
              setCategory('revenue');
              setGrouping('by_domain');
              dispatch(creatingSchedule(true));
            }}
          />
        </div>
      </div>
      {/* Create schedule */}
      {currentlyCreatingSchedule && (
        <Modal
          onClose={() => {
            dispatch(creatingSchedule(false));
          }}
        >
          <EditReportForm
            createOrUpdateMode={'create'}
            category={category}
            grouping={grouping}
          />
        </Modal>
      )}
      {/* Confirm saved */}
      {currentlyConfirmingSchedule && (
        <Modal
          onClose={() => {
            dispatch(confirmingSchedule(null));
          }}
        >
          <ConfirmSave currentSchedule={currentlyConfirmingSchedule} />
        </Modal>
      )}
    </>
  );
};
